/**
 * CollapsibleElement — F12.H T12.H.4.
 *
 * Zwijany — pojedyncza sekcja rozwijana (jak Akordeon z 1 sekcją).
 * Użycie: opcjonalne pola które domyślnie są ukryte żeby nie zaśmiecać
 * formularza („Dodatkowe informacje", „Zaawansowane ustawienia").
 *
 * Settings: { title, defaultExpanded?, variant?: 'default'|'subtle' }
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

export const CollapsibleElement: LayoutElementDefinition = {
  type: 'collapsible',
  category: 'interaktywne',
  isContainer: true,
  label: 'Zwijany',
  icon: 'ChevronsUpDown',
  defaultSettings: () => ({
    title: 'Dodatkowe informacje',
    defaultExpanded: false,
    variant: 'default',
  }),
  render: ({ element, children, mode }) => {
    const settings = (element.settings ?? {}) as {
      title?: string;
      defaultExpanded?: boolean;
      variant?: 'default' | 'subtle';
    };
    const isBuilder = mode === 'builder';
    return (
      <CollapsibleView
        title={settings.title ?? 'Zwijany blok'}
        defaultExpanded={settings.defaultExpanded ?? false}
        variant={settings.variant ?? 'default'}
        children={children}
        isBuilder={isBuilder}
        elementId={element.id}
      />
    );
  },
};

function CollapsibleView({
  title,
  defaultExpanded,
  variant,
  children,
  isBuilder,
  elementId,
}: {
  title: string;
  defaultExpanded: boolean;
  variant: 'default' | 'subtle';
  children: React.ReactNode[];
  isBuilder: boolean;
  elementId: string;
}) {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const wrapperCls = variant === 'subtle' ? 'bg-bg-secondary' : 'border border-border-primary';

  return (
    <div
      className={`rounded-lg ${wrapperCls} ${isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition' : ''}`}
      data-testid={`collapsible-element-${elementId}`}
      data-element-type="collapsible"
    >
      <button
        type="button"
        onClick={() => setIsOpen((p) => !p)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-text-primary hover:bg-bg-tertiary/50"
      >
        <span>{title}</span>
        <ChevronDown
          className={`h-4 w-4 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {isOpen && (
        <div className={`p-3 ${variant === 'subtle' ? '' : 'border-t border-border-primary'}`}>
          {children.length > 0 ? (
            <div className="space-y-3">{children}</div>
          ) : isBuilder ? (
            <p className="text-xs italic text-text-muted">(pusty — przeciągnij elementy)</p>
          ) : null}
        </div>
      )}
    </div>
  );
}
